import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { API_BASE } from '../config/api';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(true);
  const [dbCategories, setDbCategories] = useState([]);
  const [openSection, setOpenSection] = useState(null);
  const { settings } = useSettings();

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await fetch(`${API_BASE}/categories`);
        const data = await res.json();
        setDbCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching categories for footer:', err);
      }
    };
    fetchCats();
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!agreed) {
      alert('Please accept Privacy Policy to continue.');
      return;
    }
    alert('Thanks! We will contact you soon.');
    setEmail('');
  };

  const toggleSection = (key) => {
    setOpenSection(openSection === key ? null : key);
  };

  const sectionStyle = (key) => ({
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    paddingBottom: openSection === key ? '12px' : '0',
    overflow: 'hidden',
  });

  const headerStyle = (key) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 0',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    width: '100%',
    color: openSection === key ? 'var(--accent)' : 'rgba(255,255,255,0.9)',
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: 'inherit',
    textAlign: 'left',
    transition: 'color 0.2s ease',
    letterSpacing: '0.3px',
  });

  const contentStyle = (key) => ({
    overflow: 'hidden',
    maxHeight: openSection === key ? '600px' : '0',
    opacity: openSection === key ? 1 : 0,
    transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingBottom: openSection === key ? '8px' : '0',
  });

  return (
    <footer className="footer footer-v2">
      <style dangerouslySetInnerHTML={{ __html: `
        .footer-chip-row { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; padding: 4px 0; }
        .footer-chip { padding: 8px 10px; border-radius: 3px; font-size: 11px; font-weight: 700; text-decoration: none; display: flex; align-items: center; justify-content: center; gap: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; transition: all 0.25s ease; border: 1px solid rgba(255,255,255,0.15); box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
        .footer-chip:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,0.35); }
        .footer-chip:active { transform: scale(0.95); }
        .chip-purple { background: linear-gradient(135deg, #667eea, #764ba2); color: #fff; }
        .chip-blue { background: linear-gradient(135deg, #4facfe, #00f2fe); color: #fff; }
        .chip-green { background: linear-gradient(135deg, #43e97b, #38f9d7); color: #111; }
        .chip-orange { background: linear-gradient(135deg, #f093fb, #f5576c); color: #fff; }
        .chip-pink { background: linear-gradient(135deg, #fa709a, #fee140); color: #111; }
        .chip-teal { background: linear-gradient(135deg, #a18cd1, #fbc2eb); color: #111; }
        .chip-yellow { background: linear-gradient(135deg, #fccb90, #d57eeb); color: #fff; }
                .chip-red { background: linear-gradient(135deg, #ff9a9e, #fecfef); color: #111; }
        .chip-tab { background: transparent !important; border: 1px solid rgba(255,255,255,0.12) !important; color: rgba(255,255,255,0.7) !important; font-size: 12px; padding: 8px 8px; box-shadow: none !important; }
        .chip-tab:hover { background: rgba(255,255,255,0.06) !important; border-color: rgba(255,255,255,0.25) !important; color: #fff !important; transform: translateY(-1px); }
        .chip-cat { position: relative; overflow: hidden; }
        .chip-cat::after { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.15), transparent); opacity: 0; transition: opacity 0.3s; }
        .chip-cat:hover::after { opacity: 1; }
        .chip-cat:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.15) !important; }
        .chip-cat > span { font-size: 14px; flex-shrink: 0; }
      ` }} />
      <div className="footer-shell">
          {/* Brand + Social */}
          <div className="footer-top">
            <div className="footer-brand-left">
              <div className="footer-logo-mark">F</div>
              <div className="footer-brand-meta">
                <div className="footer-brand-name">{settings?.site_name || 'FACTO ELECTRONICAL'}</div>
                <div className="footer-brand-tagline">{settings?.footer_text || 'Premium LED lighting solutions'}</div>
              </div>
            </div>
            <div className="footer-top-icons" aria-label="Social links">
              {!!settings?.facebook && (
                <a href={settings.facebook} target="_blank" rel="noreferrer" aria-label="Facebook">f</a>
              )}
              {!!settings?.instagram && (
                <a href={settings.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">i</a>
              )}
              {!!settings?.twitter && (
                <a href={settings.twitter} target="_blank" rel="noreferrer" aria-label="Twitter/X">x</a>
              )}
              {!!settings?.linkedin && (
                <a href={settings.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">in</a>
              )}
            </div>
          </div>

          <div className="footer-divider" />

          {/* Desktop Link Columns */}
          <div className="footer-content">
            <div className="footer-col">
              <h4>Quick Links</h4>
              <Link to="/">Home</Link>
              <Link to="/about">About</Link>
              <Link to="/products">Products</Link>
              <Link to="/media">Media</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div className="footer-col">
              <h4>Categories</h4>
              {dbCategories.length > 0 ? (
                dbCategories.slice(0, 5).map((cat, i) => {
                  const colors = ['#667eea','#4facfe','#43e97b','#f5576c','#fee140','#a18cd1','#fccb90','#ff9a9e','#f093fb'];
                  return (
                    <Link key={cat.id} to="/products" state={{ category: cat.slug }} className="footer-cat-link" style={{ '--dot-color': colors[i % colors.length] }}>{cat.name}</Link>
                  );
                })
              ) : (
                <>
                  <Link to="/category/junction-downlights">Junction Downlights</Link>
                  <Link to="/category/panel-lights">Panel Lights</Link>
                </>
              )}
            </div>
            <div className="footer-col">
              <h4>Support</h4>
              <Link to="/blog">Blog</Link>
              <Link to="/career">Career</Link>
              <Link to="/admin-login">Admin Panel</Link>
              <Link to="/gallery">Gallery</Link>
            </div>
            <div className="footer-col">
              <h4>Contact Info</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <a href={`mailto:${settings?.email || 'Factoelectricals@gmail.com'}`} style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: 'var(--accent)', fontSize: '16px' }}>✉</span>
                  {settings?.email || 'Factoelectricals@gmail.com'}
                </a>
                <a href={`tel:${settings?.phone || '7404704959'}`} style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: 'var(--accent)', fontSize: '16px' }}>☎</span>
                  {settings?.phone || '7404704959'}
                </a>
                {settings?.address && (
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: '1.5' }}>
                    <span style={{ color: 'var(--accent)', fontSize: '16px', flexShrink: 0 }}>⌂</span>
                    {settings.address}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Accordion Links */}
          <div className="footer-mobile-links">
            <div style={sectionStyle('quick')}>
              <button onClick={() => toggleSection('quick')} style={headerStyle('quick')}>
                <span>Quick Links</span>
                <span style={{ transform: openSection === 'quick' ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: '12px' }}>▼</span>
              </button>
              <div style={contentStyle('quick')}>
                <div className="footer-chip-row">
                  <Link to="/" className="footer-chip chip-tab">Home</Link>
                  <Link to="/about" className="footer-chip chip-tab">About</Link>
                  <Link to="/products" className="footer-chip chip-tab">Products</Link>
                  <Link to="/media" className="footer-chip chip-tab">Media</Link>
                  <Link to="/contact" className="footer-chip chip-tab">Contact</Link>
                  <Link to="/admin-login" className="footer-chip chip-tab">Admin</Link>
                </div>
              </div>
            </div>
            <div style={sectionStyle('categories')}>
              <button onClick={() => toggleSection('categories')} style={headerStyle('categories')}>
                <span>Categories</span>
                <span style={{ transform: openSection === 'categories' ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: '12px' }}>▼</span>
              </button>
              <div style={contentStyle('categories')}>
                <div className="footer-chip-row">
                  {dbCategories.length > 0 ? (
                    dbCategories.map((cat, i) => {
                      const chips = ['chip-purple', 'chip-blue', 'chip-green', 'chip-orange', 'chip-pink', 'chip-teal', 'chip-yellow', 'chip-red'];
                      const icons = ['⚡', '💡', '🔌', '🏠', '🔦', '📐', '🛡️', '⭐'];
                      return (
                        <Link key={cat.id} to="/products" state={{ category: cat.slug }} className={`footer-chip chip-cat ${chips[i % chips.length]}`}><span>{icons[i % icons.length]}</span> {cat.name}</Link>
                      );
                    })
                  ) : (
                    <>
                      <Link to="/category/junction-downlights" className="footer-chip chip-cat chip-purple"><span>💡</span> Junction Downlights</Link>
                      <Link to="/category/panel-lights" className="footer-chip chip-cat chip-blue"><span>🔲</span> Panel Lights</Link>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div style={sectionStyle('support')}>
              <button onClick={() => toggleSection('support')} style={headerStyle('support')}>
                <span>Support</span>
                <span style={{ transform: openSection === 'support' ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: '12px' }}>▼</span>
              </button>
              <div style={contentStyle('support')}>
                <div className="footer-chip-row">
                  <Link to="/blog" className="footer-chip chip-tab">Blog</Link>
                  <Link to="/career" className="footer-chip chip-tab">Career</Link>
                  <Link to="/gallery" className="footer-chip chip-tab">Gallery</Link>
                  <a href={`mailto:${settings?.email || 'Factoelectricals@gmail.com'}`} className="footer-chip chip-tab">✉ Email</a>
                  <a href={`tel:${settings?.phone || '7404704959'}`} className="footer-chip chip-tab">☎ {settings?.phone || '7404704959'}</a>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-divider" />

          {/* Newsletter */}
          <div className="footer-newsletter">
            <h4>Newsletter</h4>
            <p>Get product updates and offers.</p>
            <form className="footer-newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <button type="submit">Send</button>
            </form>
            <label className="footer-newsletter-terms">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              I agree to Privacy Policy
            </label>
          </div>

          {/* Copyright */}
          <div className="footer-bottom-mini">
            {settings?.copyright || '© 2024 FACTO ELECTRONICAL. All rights reserved.'}
          </div>
      </div>
    </footer>
  );
};

export default Footer;
